import React, { useState } from 'react';
import { motion } from 'framer-motion';
import HeroSection from '../../components/common/HeroSection';

const Tools = () => {
  const [bmiData, setBmiData] = useState({ weight: '', height: '', result: null });
  const [bmrData, setBmrData] = useState({ 
    weight: '', 
    height: '', 
    age: '', 
    gender: 'male', 
    activity: 'sedentary',
    result: null 
  });
  const [calorieData, setCalorieData] = useState({ 
    goal: 'maintain', 
    result: null 
  });

  // BMI Calculator
  const calculateBMI = () => {
    if (bmiData.weight && bmiData.height) {
      const heightInMeters = bmiData.height / 100; // Convert cm to meters
      const bmi = (bmiData.weight / (heightInMeters * heightInMeters)).toFixed(1);
      let category = '';
      
     if (bmi < 18.5) category = 'Zayıf';
	 else if (bmi < 25) category = 'Normal';
	 else if (bmi < 30) category = 'Fazla Kilolu';
	 else category = 'Obez';
      
      setBmiData({ ...bmiData, result: { value: bmi, category } });
    }
  };

  // BMR Calculator (Mifflin-St Jeor Equation)
  const calculateBMR = () => {
    if (bmrData.weight && bmrData.height && bmrData.age) {
      let bmr;
      // Convert to numbers
      const weight = parseFloat(bmrData.weight);
      const height = parseFloat(bmrData.height);
      const age = parseFloat(bmrData.age);
      
      if (bmrData.gender === 'male') {
        bmr = 10 * weight + 6.25 * height - 5 * age + 5;
      } else {
        bmr = 10 * weight + 6.25 * height - 5 * age - 161;
      }
      
      // Activity multipliers
      const activityFactors = {
        'sedentary': 1.2,    // Little or no exercise
        'light': 1.375,      // Light exercise 1-3 days/week
        'moderate': 1.55,    // Moderate exercise 3-5 days/week
        'active': 1.725,     // Hard exercise 6-7 days/week
        'veryActive': 1.9    // Very hard exercise, physical job
      };
      
      const tdee = (bmr * activityFactors[bmrData.activity]).toFixed(0);
      
      setBmrData({ 
        ...bmrData, 
        result: { 
          bmr: bmr.toFixed(0), 
          tdee: tdee 
        } 
      });
    }
  };

  // Calorie Calculator based on goals
  const calculateCalories = () => {
    if (bmrData.result) {
      let calorieAdjustment = 0;
      
      switch(calorieData.goal) {
        case 'lose':
          calorieAdjustment = -500; // 500 calorie deficit for weight loss
          break;
        case 'gain':
          calorieAdjustment = 500; // 500 calorie surplus for weight gain
          break;
        case 'maintain':
        default:
          calorieAdjustment = 0;
          break;
      }
      
      const dailyCalories = parseInt(bmrData.result.tdee) + calorieAdjustment;
      
      setCalorieData({ 
        ...calorieData, 
        result: dailyCalories 
      });
    }
  };

  // Reset all calculators
  const resetCalculators = () => {
    setBmiData({ weight: '', height: '', result: null });
    setBmrData({ 
      weight: '', 
      height: '', 
      age: '', 
      gender: 'male', 
      activity: 'sedentary',
      result: null 
    });
    setCalorieData({ goal: 'maintain', result: null });
  };

  return (
    <div className="w-full">
      {/* Page Header */}
      <HeroSection 
        title="Beslenme Araçları" 
        subtitle="Sağlık ölçümlerinizi ve beslenme ihtiyaçlarınızı hesaplayın" 
      />

      <div className="section-padding bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-nihal-dark mb-4">Sağlık ve Beslenme Hesaplayıcıları</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Bu araçları kullanarak önemli sağlık ölçümlerinizi hesaplayabilir ve beslenme ihtiyaçlarınızı daha iyi anlayabilirsiniz.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* BMI Calculator */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="bg-nihal-secondary rounded-xl p-6 shadow-md"
            >
              <h3 className="text-xl font-bold text-nihal-dark mb-4">Vücut Kitle İndeksi Hesaplayıcı</h3>
              <p className="text-gray-700 mb-4">Vücut Kitle İndeksinizi hesaplayarak kilo kategorinizi değerlendirin.</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kilo (kg)</label>
                  <input
                    type="number"
                    value={bmiData.weight}
                    onChange={(e) => setBmiData({...bmiData, weight: e.target.value, result: null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                    placeholder="Kilonuzu giriniz"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Boy (cm)</label>
                  <input
                    type="number"
                    value={bmiData.height}
                    onChange={(e) => setBmiData({...bmiData, height: e.target.value, result: null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                    placeholder="Boyunuzu giriniz"
                  />
                </div>
                
                <button
                  onClick={calculateBMI}
                  className="w-full btn btn-primary"
                >
                  Hesapla
                </button>
                
                {bmiData.result && (
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-lg font-bold text-nihal-dark">Vücut Kitle İndeksiniz: {bmiData.result.value}</p>
                    <p className="text-gray-700">Kategori: {bmiData.result.category}</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* BMR Calculator */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="bg-nihal-secondary rounded-xl p-6 shadow-md"
            >
              <h3 className="text-xl font-bold text-nihal-dark mb-4">Bazal Metabolizma Hızı (BMR) ve Toplam Günlük Enerji Harcaması (TDEE) Hesaplayıcı</h3>
              <p className="text-gray-700 mb-4">Bazal Metabolizma Hızınızı ve Toplam Günlük Enerji Harcamanızı hesaplayın.</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kilo (kg)</label>
                  <input
                    type="number"
                    value={bmrData.weight}
                    onChange={(e) => setBmrData({...bmrData, weight: e.target.value, result: null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                    placeholder="Kilonuzu girin"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Boy (cm)</label>
                  <input
                    type="number"
                    value={bmrData.height}
                    onChange={(e) => setBmrData({...bmrData, height: e.target.value, result: null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                    placeholder="Boyunuzu girin"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yaş</label>
                  <input
                    type="number"
                    value={bmrData.age}
                    onChange={(e) => setBmrData({...bmrData, age: e.target.value, result: null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                    placeholder="Yaşınızı girin"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cinsiyet</label>
                  <select
                    value={bmrData.gender}
                    onChange={(e) => setBmrData({...bmrData, gender: e.target.value, result: null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  >
                    <option value="male">Erkek</option>
                    <option value="female">Kadın</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Aktivite Seviyesi</label>
                  <select
                    value={bmrData.activity}
                    onChange={(e) => setBmrData({...bmrData, activity: e.target.value, result: null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  >
					<option value="sedentary">Hareketsiz (az veya hiç egzersiz yok)</option>
					<option value="light">Hafif (haftada 1-3 gün egzersiz)</option>
					<option value="moderate">Orta (haftada 3-5 gün egzersiz)</option>
					<option value="active">Aktif (haftada 6-7 gün egzersiz)</option>
					<option value="veryActive">Çok Aktif (yoğun egzersiz veya fiziksel iş)</option>
                  </select>
                </div>
                
                <button
                  onClick={calculateBMR}
                  className="w-full btn btn-primary"
                >
                  Hesapla
                </button>
                
                {bmrData.result && (
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-sm">
                    <p className="text-lg font-bold text-nihal-dark">BMR: {bmrData.result.bmr} kcal/gün</p>
                    <p className="text-lg font-bold text-nihal-dark">TDEE: {bmrData.result.tdee} kcal/gün</p>
                    <p className="text-gray-700 mt-2">Bu, mevcut kilonuzu korumak için ihtiyacınız olan kalori miktarıdır.</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* Calorie Goal Calculator */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
              className="bg-nihal-secondary rounded-xl p-6 shadow-md"
            >
              <h3 className="text-xl font-bold text-nihal-dark mb-4">Günlük Kalori Hedefi</h3>
              <p className="text-gray-700 mb-4">Hedeflerinize göre günlük kalori alımınızı hesaplayın.</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hedef</label>
                  <select
                    value={calorieData.goal}
                    onChange={(e) => setCalorieData({...calorieData, goal: e.target.value, result: null})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nihal-primary"
                  >
                   <option value="maintain">Kilo Koruma</option>
					<option value="lose">Kilo Verme</option>
					<option value="gain">Kilo Alma</option>
                  </select>
                </div>
                
                <button
                  onClick={calculateCalories}
                  disabled={!bmrData.result}
                  className={`w-full btn ${bmrData.result ? 'btn-primary' : 'btn-secondary'} ${(bmrData.result ? '' : 'cursor-not-allowed')}`}
                >
                  Hesapla
                </button>
                
                {calorieData.result && (
				<div className="mt-4 p-4 bg-white rounded-lg shadow-sm">
					<p className="text-lg font-bold text-nihal-dark">
					Günlük Kalori: {calorieData.result} kcal
					</p>
					<p className="text-gray-700 mt-2">
					{calorieData.goal === 'lose' 
						? 'Bu, sağlıklı kilo kaybı için bir kalori açığıdır (haftada yaklaşık 0.5 kg)' 
						: calorieData.goal === 'gain' 
						? 'Bu, sağlıklı kilo alımı için bir kalori fazlasıdır' 
						: 'Bu, mevcut kilonuzu korumak için gerekli kaloridir'}
					</p>
				</div>
				)}
              </div>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
			  <h4 className="font-bold text-nihal-dark mb-2">Hızlı Referans</h4>
			  <ul className="text-sm text-gray-700 space-y-1">
			 	 <li>• 1 kg yağ ≈ 7700 kcal</li>
			 	 <li>• Sağlıklı kilo kaybı: haftada 0.5-1 kg</li>
			 	 <li>• Kilo kaybı için kalori açığı gerekir</li>
			 	 <li>• Kilo alımı için kalori fazlası gerekir</li>
			  </ul>
			  </div>
            </motion.div>
          </div>

          {/* Reset Button */}
          <div className="text-center mt-12">
            <button
              onClick={resetCalculators}
              className="btn btn-secondary"
            >
              Tüm Hesaplayıcıları Sıfırla
            </button>
          </div>

          {/* Information Section */}
          <div className="mt-16 bg-nihal-primary bg-opacity-10 rounded-2xl p-8"> <h3 className="text-2xl font-bold text-nihal-dark mb-4">Önemli Bilgiler</h3> <ul className="list-disc list-inside text-gray-700 space-y-2"> <li>Bu hesaplayıcılar standart formüllere dayalı tahminler sağlar ve başlangıç noktası olarak kullanılmalıdır.</li> <li>Bireysel ihtiyaçlar genetik, sağlık durumu ve metabolizma gibi faktörlere göre değişebilir.</li> <li>Kişiye özel beslenme planları için yetkin bir diyetisyene danışın.</li> <li>BMI bir tarama aracıdır ancak kas kütlesi, kemik yoğunluğu ve diğer faktörleri dikkate almaz.</li> <li>Sağlıklı kilo kaybı genellikle haftada 0,5-1 kg’dır.</li> </ul> </div>
        </div>
      </div>
    </div>
  );
};

export default Tools;